/* ============================================================
   Rynoka: toyland-plp-filters.js
   Client-side faceted filtering + infinite scroll for the collection PLP — no app.
   Filters product-level data (subcat / price / rating / stock / free) that Liquid
   rendered onto each [data-tl-item]. URL-synced (shareable + back/forward).

   Infinite scroll (strictly lazy): the server renders only page 1 (products_per_page,
   default 12). Loading is driven by an IntersectionObserver on the sentinel (which fires
   once per crossing, so it can't runaway) plus a cooldown-guarded scroll fallback — both
   call loadNextBatch(), which fetches EXACTLY the next page, never chained. Nothing is
   bulk-loaded up front and no fill loop exists — the next 12 arrive only on scroll.

   Filter/sort are client-side over the products loaded SO FAR (no Search & Discovery app):
   changing them re-filters/re-sorts the loaded set and, if that leaves the view short,
   the lazy loader pulls the next batch — it never fetches the whole collection at once.

   Result count: "Showing N results" = products currently shown; it starts at one batch
   (12) and grows by a batch each time the next page is appended on scroll.
   ============================================================ */
(function () {
  'use strict';

  var root = document.querySelector('[data-tl-plp]');
  if (!root) return;
  var grid = root.querySelector('[data-tl-grid]');
  if (!grid) return;

  var state = { subcat: new Set(), price: '', rating: 0, instock: false, free: false, sort: 'featured' };

  var items = [];
  var seq = 0;
  function refreshItems() {
    items = Array.prototype.slice.call(grid.querySelectorAll('[data-tl-item]'));
    items.forEach(function (el) { if (el._tlSeq == null) el._tlSeq = seq++; });
  }
  refreshItems();

  function num(el, attr) { return parseFloat(el.getAttribute(attr) || '0') || 0; }
  function esc(v) { return (window.CSS && CSS.escape) ? CSS.escape(v) : String(v).replace(/["\\\]]/g, '\\$&'); }
  function escapeHtml(s) { var d = document.createElement('div'); d.textContent = s; return d.innerHTML; }

  function activeCount() {
    return state.subcat.size + (state.price ? 1 : 0) + (state.rating ? 1 : 0) + (state.instock ? 1 : 0) + (state.free ? 1 : 0);
  }

  /* ---- URL <-> state ---- */
  function readURL() {
    var p = new URLSearchParams(location.search);
    state.subcat = new Set((p.get('subcat') || '').split(',').filter(Boolean));
    state.price = p.get('price') || '';
    state.rating = parseInt(p.get('rating') || '0', 10) || 0;
    state.instock = p.get('instock') === '1';
    state.free = p.get('free') === '1';
    state.sort = p.get('sort') || 'featured';
  }
  function writeURL(push) {
    var p = new URLSearchParams(location.search);
    ['subcat', 'price', 'rating', 'instock', 'free', 'sort'].forEach(function (k) { p.delete(k); });
    if (state.subcat.size) p.set('subcat', Array.from(state.subcat).join(','));
    if (state.price) p.set('price', state.price);
    if (state.rating) p.set('rating', String(state.rating));
    if (state.instock) p.set('instock', '1');
    if (state.free) p.set('free', '1');
    if (state.sort && state.sort !== 'featured') p.set('sort', state.sort);
    var qs = p.toString();
    var url = location.pathname + (qs ? '?' + qs : '') + location.hash;
    try { history[push ? 'pushState' : 'replaceState']({ tlplp: true }, '', url); } catch (e) {}
  }

  /* ---- matching ---- */
  function matches(el) {
    if (state.subcat.size) {
      var subs = (el.getAttribute('data-subcat') || '').split(/\s+/).filter(Boolean);
      if (!subs.some(function (s) { return state.subcat.has(s); })) return false;
    }
    if (state.price) {
      var pr = num(el, 'data-price');
      var mm = state.price.split('-');
      var min = parseInt(mm[0], 10) || 0;
      var max = (mm[1] === undefined || mm[1] === '') ? Infinity : parseInt(mm[1], 10);
      if (pr < min) return false;
      if (max !== Infinity && pr >= max) return false;
    }
    if (state.rating && num(el, 'data-rating') < state.rating) return false;
    if (state.instock && el.getAttribute('data-available') !== 'true') return false;
    if (state.free && el.getAttribute('data-free') !== '1') return false;
    return true;
  }

  /* ---- apply (filter -> sort -> ui) ---- */
  function apply() {
    var visible = [];
    items.forEach(function (el) {
      var ok = matches(el);
      el.hidden = !ok;
      if (ok) visible.push(el);
    });

    /* Merchandising rank (custom.ranking, 1 = first) applies to the DEFAULT order only.
       Once the shopper explicitly sorts by price/rating/newest, that choice wins outright
       — a pinned block riding above a price sort just reads as the sort being broken.
       Unranked products sort after every ranked one, in server order.
       This runs again after each infinite-scroll batch, which is what pulls a ranked
       product up from a later page once it loads. */
    function rank(el) {
      var r = parseInt(el.getAttribute('data-ranking'), 10);
      return r > 0 ? r : Infinity;
    }

    visible.sort(function (a, b) {
      switch (state.sort) {
        case 'low': return num(a, 'data-price') - num(b, 'data-price');
        case 'high': return num(b, 'data-price') - num(a, 'data-price');
        case 'rating': return num(b, 'data-rating') - num(a, 'data-rating');
        case 'new': return num(b, 'data-date') - num(a, 'data-date');
        default: {
          var ra = rank(a);
          var rb = rank(b);
          if (ra !== rb) return ra - rb;
          return a._tlSeq - b._tlSeq;
        }
      }
    });
    visible.forEach(function (el) { grid.appendChild(el); });

    updateCounts();
    // Count = items currently shown (grows 12 -> 24 -> ... as batches stream in on scroll,
    // and jumps to the matched total once a filter pulls in the whole collection).
    setText('[data-tl-result-count]', visible.length);

    var active = activeCount();
    root.querySelectorAll('[data-tl-filter-count]').forEach(function (e) { e.textContent = active; e.hidden = active === 0; });
    var noRes = root.querySelector('[data-tl-noresults]');
    // Only claim "no results" once nothing more can arrive.
    if (noRes) noRes.hidden = !(visible.length === 0 && !hasMore());
    updateInfiniteUI();
    renderChips();
    syncInputs();
  }

  function setText(sel, val) { root.querySelectorAll(sel).forEach(function (e) { e.textContent = val; }); }

  function updateCounts() {
    var counts = {};
    items.forEach(function (el) {
      (el.getAttribute('data-subcat') || '').split(/\s+/).filter(Boolean).forEach(function (s) { counts[s] = (counts[s] || 0) + 1; });
    });
    root.querySelectorAll('[data-tl-count]').forEach(function (span) {
      var t = span.getAttribute('data-tl-count');
      span.textContent = counts[t] != null ? counts[t] : 0;
    });
  }

  function facetLabel(f, v) {
    var input = root.querySelector('[data-tl-facet="' + f + '"][data-value="' + esc(v) + '"]');
    if (!input) return v;
    var row = input.closest('.tl-facet__row');
    if (row) {
      var lbl = row.querySelector('.tl-facet__label:not(.tl-facet__label--muted)');
      if (lbl) return lbl.textContent.trim();
    }
    return v;
  }

  function renderChips() {
    var wrap = root.querySelector('[data-tl-chips]');
    if (!wrap) return;
    var chips = [];
    state.subcat.forEach(function (t) { chips.push({ f: 'subcat', v: t, label: facetLabel('subcat', t) }); });
    if (state.price) chips.push({ f: 'price', v: state.price, label: facetLabel('price', state.price) });
    if (state.rating) chips.push({ f: 'rating', v: String(state.rating), label: state.rating + '★ & up' });
    if (state.instock) chips.push({ f: 'instock', v: '1', label: facetLabel('instock', '1') });
    if (state.free) chips.push({ f: 'free', v: '1', label: facetLabel('free', '1') });

    wrap.innerHTML = '';
    if (!chips.length) { wrap.hidden = true; return; }
    wrap.hidden = false;
    chips.forEach(function (c) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'tl-plp__chip';
      b.setAttribute('aria-label', 'Remove ' + c.label);
      b.innerHTML = escapeHtml(c.label) + ' <span aria-hidden="true">✕</span>';
      b.addEventListener('click', function () { clearFacet(c.f, c.v); });
      wrap.appendChild(b);
    });
    var clr = document.createElement('button');
    clr.type = 'button';
    clr.className = 'tl-plp__clearall';
    clr.textContent = 'Clear all';
    clr.addEventListener('click', clearAll);
    wrap.appendChild(clr);
  }

  function syncInputs() {
    root.querySelectorAll('[data-tl-facet]').forEach(function (input) {
      var f = input.getAttribute('data-tl-facet');
      var v = input.getAttribute('data-value');
      var on = false;
      if (f === 'subcat') on = state.subcat.has(v);
      else if (f === 'price') on = state.price ? (state.price === v) : (v === '');
      else if (f === 'rating') on = state.rating ? (String(state.rating) === v) : (v === '0');
      else if (f === 'instock') on = state.instock;
      else if (f === 'free') on = state.free;
      input.checked = on;
    });
    root.querySelectorAll('[data-tl-sort]').forEach(function (s) { s.value = state.sort; });
    updateSortDropdowns();
    syncFacetGroups();
  }

  /* ---- Nested facet groups (shop page): main category → sub-category accordion ----
     Only the shop page renders these; the category PLP has a flat facet list and every
     query below simply matches nothing there. Groups start collapsed so the whole tree
     fits one mobile screen, and a group auto-opens when one of its sub-categories is
     active (deep link, back/forward, chip removal) — unless the shopper has collapsed
     that group themselves, in which case their choice sticks. */
  function setGroupOpen(group, open) {
    var head = group.querySelector('[data-tl-group-toggle]');
    var body = group.querySelector('[data-tl-group-body]');
    if (!head || !body) return;
    head.setAttribute('aria-expanded', open ? 'true' : 'false');
    body.hidden = !open;
    group.classList.toggle('is-open', open);
  }

  function syncFacetGroups() {
    root.querySelectorAll('[data-tl-facet-group]').forEach(function (group) {
      if (group._tlToggledByUser) return;
      var hasActive = Array.prototype.some.call(
        group.querySelectorAll('[data-tl-facet="subcat"]'),
        function (input) { return state.subcat.has(input.getAttribute('data-value')); }
      );
      if (hasActive) setGroupOpen(group, true);
    });
  }

  root.addEventListener('click', function (e) {
    var head = e.target.closest && e.target.closest('[data-tl-group-toggle]');
    if (!head) return;
    var group = head.closest('[data-tl-facet-group]');
    if (!group) return;
    group._tlToggledByUser = true;
    setGroupOpen(group, head.getAttribute('aria-expanded') !== 'true');
  });

  /* ---- Custom sort dropdown (on-brand replacement for the native <select>) ----
     Reuses the shared .tl-sort* styles (toyland-redesign.css). The native <select>
     stays in the DOM as the state anchor: options selected here write its value and
     dispatch 'change', so the existing sort state machine above is untouched. */
  function closeAllSortDropdowns(except) {
    root.querySelectorAll('[data-tl-sortdd]').forEach(function (dd) {
      if (dd === except) return;
      var m = dd.querySelector('.tl-sort__menu');
      var t = dd.querySelector('.tl-sort__trigger');
      if (m) m.hidden = true;
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  }

  function updateSortDropdowns() {
    root.querySelectorAll('[data-tl-sortdd]').forEach(function (dd) {
      var select = dd.querySelector('select[data-tl-sort]');
      if (!select) return;
      var current = select.value;
      var text = '';
      Array.prototype.forEach.call(select.options, function (o) { if (o.value === current) text = o.textContent; });
      var valueEl = dd.querySelector('[data-tl-sortdd-value]');
      if (valueEl) valueEl.textContent = text;
      dd.querySelectorAll('.tl-sort__option').forEach(function (li) {
        var on = li.getAttribute('data-value') === current;
        li.classList.toggle('is-active', on);
        li.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    });
  }

  var sortDropdownSeq = 0;
  function initSortDropdowns() {
    root.querySelectorAll('[data-tl-sortdd]').forEach(function (dd) {
      if (dd._tlSortInit) return;
      var select = dd.querySelector('select[data-tl-sort]');
      if (!select) return;
      dd._tlSortInit = true;

      // Keep the select as the anchor, but out of the a11y tree + tab order.
      select.setAttribute('tabindex', '-1');
      select.setAttribute('aria-hidden', 'true');

      var valueId = 'tl-sortdd-val-' + (sortDropdownSeq++);
      var labelId = dd.getAttribute('data-label-id') || '';

      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'tl-sort__trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-labelledby', (labelId ? labelId + ' ' : '') + valueId);
      trigger.innerHTML =
        '<span class="tl-sort__value" data-tl-sortdd-value id="' + valueId + '"></span>' +
        '<svg class="tl-sort__caret" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>';

      var menu = document.createElement('ul');
      menu.className = 'tl-sort__menu';
      menu.setAttribute('role', 'listbox');
      menu.setAttribute('tabindex', '-1');
      menu.hidden = true;
      var al = select.getAttribute('aria-label');
      if (al) menu.setAttribute('aria-label', al);
      Array.prototype.forEach.call(select.options, function (opt) {
        var li = document.createElement('li');
        li.className = 'tl-sort__option';
        li.setAttribute('role', 'option');
        li.setAttribute('data-value', opt.value);
        li.setAttribute('aria-selected', 'false');
        li.setAttribute('tabindex', '-1');
        li.innerHTML = '<span></span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
        li.firstChild.textContent = opt.textContent;
        menu.appendChild(li);
      });

      dd.appendChild(trigger);
      dd.appendChild(menu);

      function opts() { return Array.prototype.slice.call(menu.querySelectorAll('.tl-sort__option')); }
      function isOpen() { return !menu.hidden; }
      function open() {
        closeAllSortDropdowns(dd);
        menu.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        var active = menu.querySelector('.tl-sort__option.is-active') || menu.firstElementChild;
        if (active) active.focus();
      }
      function close(focusTrigger) {
        menu.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        if (focusTrigger) trigger.focus();
      }
      function choose(v) {
        if (select.value !== v) {
          select.value = v;
          select.dispatchEvent(new Event('change', { bubbles: true })); // → sort state machine
        }
        updateSortDropdowns();
      }

      trigger.addEventListener('click', function () { isOpen() ? close(false) : open(); });
      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
      });
      menu.addEventListener('click', function (e) {
        var li = e.target.closest('.tl-sort__option');
        if (!li) return;
        choose(li.getAttribute('data-value'));
        close(true);
      });
      menu.addEventListener('keydown', function (e) {
        var list = opts();
        var i = list.indexOf(document.activeElement);
        if (e.key === 'ArrowDown') { e.preventDefault(); (list[i + 1] || list[0]).focus(); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); (list[i - 1] || list[list.length - 1]).focus(); }
        else if (e.key === 'Home') { e.preventDefault(); list[0].focus(); }
        else if (e.key === 'End') { e.preventDefault(); list[list.length - 1].focus(); }
        else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          var cur = document.activeElement;
          if (cur && cur.classList.contains('tl-sort__option')) { choose(cur.getAttribute('data-value')); close(true); }
        }
        else if (e.key === 'Escape') { e.preventDefault(); close(true); }
        else if (e.key === 'Tab') { close(false); }
      });
    });
    updateSortDropdowns();
  }
  document.addEventListener('click', function (e) {
    if (!(e.target.closest && e.target.closest('[data-tl-sortdd]'))) closeAllSortDropdowns(null);
  });

  /* ---- state mutations ---- */
  function afterChange() {
    writeURL(true);
    apply();
    // Purely lazy: filter/sort re-run over the products loaded so far. More matching
    // products appear as the shopper scrolls and the next batches load — no bulk fetch.
  }
  function onFacetChange(input) {
    var f = input.getAttribute('data-tl-facet');
    var v = input.getAttribute('data-value');
    if (f === 'subcat') { if (input.checked) state.subcat.add(v); else state.subcat.delete(v); }
    else if (f === 'price') { state.price = v; }
    else if (f === 'rating') { state.rating = parseInt(v, 10) || 0; }
    else if (f === 'instock') { state.instock = input.checked; }
    else if (f === 'free') { state.free = input.checked; }
    afterChange();
  }
  function clearFacet(f, v) {
    if (f === 'subcat') state.subcat.delete(v);
    else if (f === 'price') state.price = '';
    else if (f === 'rating') state.rating = 0;
    else if (f === 'instock') state.instock = false;
    else if (f === 'free') state.free = false;
    afterChange();
  }
  function clearAll() {
    state.subcat = new Set();
    state.price = '';
    state.rating = 0;
    state.instock = false;
    state.free = false;
    writeURL(true);
    apply();
  }

  /* ---- sub-category page: checkboxes that navigate instead of filtering ----
     A sub-category page only holds one sub-category's products, so filtering in
     place could only ever narrow to zero. The boxes look and behave like the
     department page's, but ticking one hands the whole selection to the department
     page — the only page that can actually show several sub-categories at once.
     Untick everything and you just land on the unfiltered department. */
  function onSubcatNav(input) {
    var wrap = input.closest('[data-tl-subcat-nav]');
    if (!wrap) return;
    var base = wrap.getAttribute('data-tl-subcat-nav') || '';
    if (!base) return;
    var picked = Array.prototype.slice
      .call(wrap.querySelectorAll('[data-tl-subcat-navitem]'))
      .filter(function (i) { return i.checked; })
      .map(function (i) { return i.getAttribute('data-value'); })
      .filter(Boolean);
    window.location.href = picked.length ? base + '?subcat=' + encodeURIComponent(picked.join(',')) : base;
  }

  /* ---- events (delegated) ---- */
  root.addEventListener('change', function (e) {
    var nav = e.target.closest && e.target.closest('[data-tl-subcat-navitem]');
    if (nav) { onSubcatNav(nav); return; }
    var input = e.target.closest && e.target.closest('[data-tl-facet]');
    if (input) { onFacetChange(input); return; }
    var sort = e.target.closest && e.target.closest('[data-tl-sort]');
    if (sort) { state.sort = sort.value; afterChange(); }
  });
  root.querySelectorAll('[data-tl-clear]').forEach(function (b) { b.addEventListener('click', clearAll); });

  /* ---- mobile drawer ---- */
  var drawer = root.querySelector('[data-tl-drawer]');
  function openDrawer() { if (drawer) { drawer.hidden = false; document.body.style.overflow = 'hidden'; } }
  function closeDrawer() { if (drawer) { drawer.hidden = true; document.body.style.overflow = ''; } }
  root.querySelectorAll('[data-tl-open-filters]').forEach(function (b) { b.addEventListener('click', openDrawer); });
  root.querySelectorAll('[data-tl-close-filters]').forEach(function (b) { b.addEventListener('click', closeDrawer); });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && drawer && !drawer.hidden) closeDrawer(); });

  /* ---- infinite scroll (server pagination + re-filter, no button) ---- */
  var sentinel = root.querySelector('[data-tl-sentinel]');
  var spinner = root.querySelector('[data-tl-spinner]');
  var infinite = root.querySelector('[data-tl-infinite]');
  var isFetching = false;

  function hasMore() { return !!(sentinel && sentinel.getAttribute('data-next-url')); }
  function setSpinner() { if (spinner) spinner.hidden = !isFetching; }
  function updateInfiniteUI() { if (infinite) infinite.style.display = (hasMore() || isFetching) ? '' : 'none'; }

  function fetchNextPage() {
    var url = sentinel && sentinel.getAttribute('data-next-url');
    if (!url || isFetching) return Promise.resolve(false);
    isFetching = true; setSpinner();
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.querySelector('[data-tl-grid]');
        if (newGrid) {
          Array.prototype.forEach.call(newGrid.querySelectorAll('[data-tl-item]'), function (el) {
            grid.appendChild(document.importNode(el, true));
          });
        }
        var ns = doc.querySelector('[data-tl-sentinel]');
        var nu = ns && ns.getAttribute('data-next-url');
        if (nu) sentinel.setAttribute('data-next-url', nu);
        else sentinel.removeAttribute('data-next-url');
        refreshItems();
        isFetching = false; setSpinner();
        apply();
        return true;
      })
      .catch(function () { isFetching = false; setSpinner(); updateInfiniteUI(); return false; });
  }

  // Small pre-load distance below the fold, so the first paint stays at one batch (12)
  // and pages are never pulled in far ahead.
  var LOAD_MARGIN = 250;
  function nearBottom() {
    if (!sentinel || !hasMore()) return false;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    var r = sentinel.getBoundingClientRect();
    // A hidden/unrendered sentinel reports an all-zero rect, which would read as
    // "always near bottom" and fire a fetch on every scroll anywhere on the page.
    // Fall back to document scroll math instead of trusting that rect.
    if (r.top === 0 && r.height === 0 && r.width === 0) {
      var se = document.scrollingElement || document.documentElement;
      return se.scrollHeight - (se.scrollTop + vh) <= LOAD_MARGIN;
    }
    return r.top <= vh + LOAD_MARGIN;
  }

  // Load EXACTLY the next batch (12). One page per trigger — NEVER chained — plus a short
  // cooldown so a fast scroll or a layout shift can't stack fetches. This is the whole
  // loader: there is no fill/prefetch loop, so nothing is ever bulk-loaded up front.
  var lastLoadAt = 0;
  function loadNextBatch() {
    if (isFetching || !hasMore()) return;
    if (Date.now() - lastLoadAt < 350) return;
    if (!nearBottom()) return;
    lastLoadAt = Date.now();
    fetchNextPage();
  }

  var scrollTicking = false;
  function onScroll() {
    if (scrollTicking) return;
    scrollTicking = true;
    (window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); })(function () {
      scrollTicking = false;
      loadNextBatch();
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  // `scroll` never fires when the page is too short to scroll (e.g. an active filter
  // hides most loaded cards). A downward wheel / touch drag is still a scroll gesture:
  // let it pull the next batch — same guards, one page per gesture, no auto loop.
  window.addEventListener('wheel', function (e) { if (e.deltaY > 0) onScroll(); }, { passive: true });
  window.addEventListener('touchmove', onScroll, { passive: true });

  // Sidebar wheel routing. The sticky sidebar scrolls INDEPENDENTLY and must NOT drag
  // the product listing with it: `overscroll-behavior: contain` (CSS) stops the filter's
  // scroll from spilling onto the page once it reaches its top/bottom edge. The ONE case
  // that still needs help is when the facets FIT (nothing to scroll) — there `contain`
  // would otherwise trap the wheel and freeze the page under the cursor, so we forward
  // the wheel to the window ourselves. When the sidebar CAN scroll we do nothing and let
  // it scroll on its own, keeping the listing perfectly still.
  var plpSidebar = root.querySelector('.tl-plp__sidebar');
  if (plpSidebar) {
    plpSidebar.addEventListener('wheel', function (e) {
      if (e.ctrlKey) return; // pinch-zoom
      var canScroll = plpSidebar.scrollHeight > plpSidebar.clientHeight + 1;
      if (canScroll) return; // sidebar handles its own scroll; never move the page
      var dy = e.deltaY;
      if (e.deltaMode === 1) dy *= 16;                       // lines → ~px
      else if (e.deltaMode === 2) dy *= plpSidebar.clientHeight; // pages → px
      window.scrollBy(0, dy);
      e.preventDefault();
      if (dy > 0) onScroll();
    }, { passive: false });
  }

  // Primary trigger: the sentinel entering view. IntersectionObserver fires once per
  // crossing, so after a batch is appended (pushing the sentinel back out of range) it
  // only re-fires when the shopper scrolls down again — it cannot runaway into a bulk load.
  if (sentinel && 'IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      if (entries.some(function (en) { return en.isIntersecting; })) loadNextBatch();
    }, { root: null, rootMargin: LOAD_MARGIN + 'px 0px', threshold: 0 });
    io.observe(sentinel);
  }

  /* ---- history + init ---- */
  window.addEventListener('popstate', function () { readURL(); apply(); });
  initSortDropdowns();
  readURL();
  writeURL(false);
  apply();
  // Nothing else runs at init: the server rendered only page 1 (12 products). The next
  // batch is fetched only when — and each time — the shopper scrolls near the sentinel.
})();
